
import { computed, defineComponent, getCurrentInstance, onBeforeUnmount, reactive, ref, watch } from 'vue'
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import { hasHotWindowsAccess } from '@/helpers/gcb2/hotWindowsAccess'
import config from '@root/config'
import useRouter from '@/hooks/useRouter'
import * as hotWindowsApi from '../api'
import { getCascadesByProject } from '@/api/networkFixer'
import { buildSendOptions } from '../sendChannels'
import BestCandidate from '../components/BestCandidate.vue'
import ClientDrawer from '../components/ClientDrawer.vue'
import DayCard from '../components/DayCard.vue'
import HwIcon from '../components/HwIcon.vue'
import TrialOfferModal from '@/modules/marketplace/components/TrialOfferModal.vue'
import { findTrialOffer, trialPeriodLabel } from '@/modules/marketplace/trialOffers'
import { activateIntegrationTrial } from '@/api/marketplace'
import { useMarketplaceStore } from '@/store/stores/marketplace'
import OfferModal from '../components/OfferModal.vue'
import ServicesDrawer from '../components/ServicesDrawer.vue'
import SettingsDrawer from '../components/SettingsDrawer.vue'
import {
    activeFilterCount,
    addDays,
    bookingLength,
    candidateReason,
    candidatesLabel,
    defaultServiceIds,
    filterSummary,
    loadSavedFilters,
    saveFilters,
    formatDuration,
    formatLongDate,
    formatMoney,
    formatShortDate,
    formatUpdatedAt,
    fromMinutes,
    plural,
    preferredStart,
    projectToday,
    startBounds,
    toMinutes,
    uniqueCandidateCount,
    visitsLabel,
} from '../helpers'
import {
    DayKey,
    HotWindow,
    HotWindowCandidate,
    HotWindowsData,
    HotWindowsFilters,
    HotWindowsSettings,
    HotWindowsTaskType,
    ServiceCatalog,
} from '../types'

const DAY_KEYS: DayKey[] = ['today', 'tomorrow']
const TASK_TYPES: HotWindowsTaskType[] = ['transfer', 'base']

const DEFAULT_SETTINGS: HotWindowsSettings = {
    minGapMinutes: 60,
    maxCandidatesPerWindow: 5,
    baseMinDaysSinceService: 1,
    baseMaxCycleDaysAhead: 15,
    baseMaxCycleDaysAfter: 25,
    baseMaxDaysSinceService: 45,
    startedWindowOffsetMinutes: 30,
    historyMonths: 12,
    serviceList: null,
}

type Layer = 'offer' | 'client' | 'settings' | 'services' | null
type ScreenMode = 'loading' | 'live' | 'gate'

export default defineComponent({
    name: 'HotWindows',
    components: { Breadcrumbs, DayCard, BestCandidate, OfferModal, ClientDrawer, SettingsDrawer, ServicesDrawer, HwIcon, TrialOfferModal },
    setup() {
        const root = getCurrentInstance()!.proxy
        const { route, router } = useRouter(root)
        const marketplaceStore = useMarketplaceStore()
        const projectId = computed<string>(() => route.value.params.id)
        const project = computed<any>(() => root.$store.getters.projectById(projectId.value))

        const day = ref<DayKey>('today')
        const task = ref<HotWindowsTaskType>('base')
        const windowId = ref<string | null>(null)
        const serviceId = ref<string | null>(null)
        const serviceSearch = ref('')
        // null — начало ещё не трогали, считаем предложенное.
        const startMinutes = ref<number | null>(null)
        // Сдвиг «Следующий кандидат» по каждой паре окно+услуга+задача.
        const cursor = ref<Record<string, number>>({})

        const settings = ref<HotWindowsSettings>({ ...DEFAULT_SETTINGS })
        const catalog = ref<ServiceCatalog>({ services: [], serviceList: null })
        const catalogLoaded = ref(false)
        // Фильтры живут между заходами на страницу — в браузере, по проекту.
        const filters = ref<HotWindowsFilters>(loadSavedFilters(route.value.params.id))

        const layer = ref<Layer>(null)
        const servicesMode = ref<'settings' | 'filter'>('filter')
        const offerCandidate = ref<HotWindowCandidate | null>(null)
        const profileCandidate = ref<HotWindowCandidate | null>(null)

        /**
         * Кому уже отправили предложение в этой сессии. Сервер отметки не
         * хранит: подбор пересчитывается каждый раз заново.
         */
        const sent = ref<string[]>([])

        // Наборы «день + задача» грузятся отдельно, переключение не ждёт сеть.
        const datasets = ref<Record<string, HotWindowsData | null>>({})
        const loading = ref<Record<string, boolean>>({})
        const loadError = ref(false)
        // Время ответа по часам администратора: `updated_at` функции — время
        // салона без пояса и у админа в другом поясе уехало бы на разницу.
        const fetchedAt = ref<Record<string, number>>({})
        // Номер пересчёта: ответ старого запроса после смены фильтров не
        // должен перетирать новый.
        let generation = 0

        const mode = ref<ScreenMode>('loading')
        const trialOpen = ref(false)
        const activatingTrial = ref(false)
        const closeTrial = () => {
            if (activatingTrial.value) return
            trialOpen.value = false
        }
        const hotWindowsTrial = findTrialOffer('hotWindows')
        const hotWindowsIntegration = computed(() => {
            const moduleId = config.marketplaceModulesIds[config.buildType]?.hotWindows
            if (!moduleId || !projectId.value) return null
            return (
                marketplaceStore
                    .allIntgrations(projectId.value)
                    .find(item => String(item.id) === String(moduleId)) ?? null
            )
        })
        const showTrialButton = computed(
            () => Boolean(hotWindowsIntegration.value) && hotWindowsIntegration.value?.isTrialUsed !== true,
        )
        async function loadTrialStatus() {
            if (!projectId.value) return
            await marketplaceStore.fetchIntegrations(projectId.value)
        }
        async function activateTrial() {
            const integrationId = hotWindowsIntegration.value?.id
            if (activatingTrial.value || !projectId.value || !integrationId) return
            activatingTrial.value = true
            const { error } = await activateIntegrationTrial({
                projectId: projectId.value,
                integrationId: String(integrationId),
            })
            activatingTrial.value = false
            if (error) {
                root.$store.dispatch('callNotify', 'Не удалось активировать пробный период')
                return
            }
            trialOpen.value = false
            await marketplaceStore.fetchIntegrations(projectId.value)
            await root.$store.dispatch('project/fetchProject', projectId.value)
        }
        const trialButtonText = hotWindowsTrial
            ? `Попробовать ${trialPeriodLabel(hotWindowsTrial.days)}`
            : 'Попробовать 5 дней'

        const dates = computed<Record<DayKey, string>>(() => {
            const today = projectToday(project.value?.timezone)
            return { today, tomorrow: addDays(today, 1) }
        })
        const datasetKey = (dayKey: DayKey, taskType: HotWindowsTaskType) => `${dayKey}|${taskType}`
        const currentKey = computed(() => datasetKey(day.value, task.value))
        const currentData = computed(() => datasets.value[currentKey.value] ?? null)
        const currentDate = computed(() => dates.value[day.value])
        const isCurrentLoading = computed(() => Boolean(loading.value[currentKey.value]))
        const isDayLoading = (dayKey: DayKey) => TASK_TYPES.some(type => loading.value[datasetKey(dayKey, type)])

        /**
         * Окна дня для задачи — ровно выдача функции. Окна у задач разные:
         * функция отдаёт окно только там, где под задачу есть кандидаты.
         * Подмешивать окна другой задачи пустыми нельзя — список перестаёт
         * сходиться с расчётом (лишние мастера с «Нет подходящих услуг»).
         */
        function dayWindows(dayKey: DayKey, taskType: HotWindowsTaskType = task.value): HotWindow[] {
            const own = datasets.value[datasetKey(dayKey, taskType)]?.windows ?? []
            return [...own].sort(
                (a, b) => a.master.localeCompare(b.master, 'ru') || toMinutes(a.time) - toMinutes(b.time)
            )
        }
        const taskCount = (dayKey: DayKey, taskType: HotWindowsTaskType) =>
            uniqueCandidateCount(datasets.value[datasetKey(dayKey, taskType)])

        const windows = computed(() => dayWindows(day.value))
        // Без явного выбора встаём на первое окно, где есть что предложить.
        const currentWindow = computed(
            () =>
                windows.value.find(item => item.id === windowId.value) ??
                windows.value.find(item => item.variants.length) ??
                windows.value[0] ??
                null
        )
        const variants = computed(() => currentWindow.value?.variants ?? [])
        const visibleVariants = computed(() => {
            const query = variants.value.length >= 6 ? serviceSearch.value.trim().toLowerCase() : ''
            return variants.value.filter(variant => variant.service.name.toLowerCase().includes(query))
        })
        const currentVariant = computed(
            () => variants.value.find(variant => variant.service.id === serviceId.value) ?? variants.value[0] ?? null
        )
        // Функция уже отдаёт кандидатов по убыванию score — порядок не трогаем.
        const candidates = computed(() => currentVariant.value?.candidates ?? [])
        const selectionKey = computed(
            () => `${currentWindow.value?.id}|${currentVariant.value?.service.id}|${task.value}`
        )
        const best = computed(() => {
            const list = candidates.value
            return list.length ? list[(cursor.value[selectionKey.value] ?? 0) % list.length] : null
        })
        const others = computed(() => candidates.value.filter(candidate => candidate !== best.value))

        const length = computed(() =>
            currentVariant.value ? bookingLength(best.value, currentVariant.value.service) : 0
        )
        const bounds = computed(() =>
            currentWindow.value ? startBounds(currentWindow.value, length.value) : { min: 0, max: 0 }
        )
        // Выбранное начало держим, пока вся запись помещается в окно.
        const start = computed(() => {
            const chosen = startMinutes.value
            if (chosen !== null && chosen >= bounds.value.min && chosen <= bounds.value.max) return chosen
            return currentWindow.value ? preferredStart(best.value, currentWindow.value, length.value) : 0
        })

        async function loadDataset(dayKey: DayKey, taskType: HotWindowsTaskType, force = false, refresh = false) {
            const key = datasetKey(dayKey, taskType)
            if (!force && (datasets.value[key] || loading.value[key])) return
            const run = generation
            loading.value = { ...loading.value, [key]: true }
            const { data, error } = await hotWindowsApi.getHotWindows({
                projectId: projectId.value,
                referenceDate: dates.value[dayKey],
                taskType,
                serviceIds: filters.value.serviceIds,
                minPrice: filters.value.minPrice,
                refresh,
            })
            if (run !== generation) return
            loading.value = { ...loading.value, [key]: false }
            if (error || !data) {
                loadError.value = true
                return
            }
            loadError.value = false
            datasets.value = { ...datasets.value, [key]: data }
            fetchedAt.value = { ...fetchedAt.value, [key]: Date.now() }
        }

        function loadAll(force = false, refresh = false) {
            if (force) {
                generation += 1
                loading.value = {}
                cursor.value = {}
                startMinutes.value = null
            }
            // Сначала то, на что смотрит админ, остальное — следом.
            const order = [datasetKey(day.value, task.value)]
            for (const dayKey of DAY_KEYS) for (const type of TASK_TYPES) {
                const key = datasetKey(dayKey, type)
                if (!order.includes(key)) order.push(key)
            }
            return Promise.all(
                order.map(key => {
                    const [dayKey, type] = key.split('|') as [DayKey, HotWindowsTaskType]
                    return loadDataset(dayKey, type, force, refresh)
                })
            )
        }

        async function loadSettings() {
            const { data } = await hotWindowsApi.getSettings(projectId.value)
            if (data) settings.value = data
        }
        // Каскады проекта — для «Канала отправки» в модалке. Не загрузились
        // (например, нет прав на настройки каналов) — остаются канал по
        // умолчанию и каналы проекта.
        const cascades = ref<any[]>([])
        async function loadCascades() {
            const { data } = await getCascadesByProject(projectId.value)
            cascades.value = Array.isArray(data) ? data : []
        }
        const sendOptions = computed(() => buildSendOptions(project.value?.connectors ?? [], cascades.value))

        async function loadCatalog() {
            const { data } = await hotWindowsApi.getServiceCatalog(projectId.value)
            if (data) catalog.value = data
            catalogLoaded.value = Boolean(data)
        }

        function buyModule() {
            const moduleId = config.marketplaceModulesIds[config.buildType]?.hotWindows
            if (!moduleId || !projectId.value) return
            router.push(`/project/${projectId.value}/marketplace/integration/${moduleId}`).catch(() => {})
        }

        function selectDayTask(payload: { day: DayKey; task: HotWindowsTaskType }) {
            day.value = payload.day
            task.value = payload.task
            serviceId.value = null
            startMinutes.value = null
            serviceSearch.value = ''
            loadDataset(payload.day, payload.task)
        }
        function selectWindow(id: string) {
            windowId.value = id
            serviceId.value = null
            startMinutes.value = null
            serviceSearch.value = ''
        }
        function selectService(id: string) {
            serviceId.value = id
            startMinutes.value = null
        }
        function changeStart(event: Event) {
            const input = event.target as HTMLInputElement
            const value = toMinutes(input.value)
            if (!Number.isFinite(value) || value < bounds.value.min || value > bounds.value.max) {
                showToast('Выберите время, чтобы вся услуга помещалась в окно')
                input.value = fromMinutes(start.value)
                return
            }
            startMinutes.value = value
        }
        function skipCandidate() {
            const count = Math.max(1, candidates.value.length)
            cursor.value = {
                ...cursor.value,
                [selectionKey.value]: ((cursor.value[selectionKey.value] ?? 0) + 1) % count,
            }
            startMinutes.value = null
        }

        const sentKey = (candidate: HotWindowCandidate) => `${day.value}|${candidate.clientId}`
        const isSent = (candidate: HotWindowCandidate) => sent.value.includes(sentKey(candidate))

        function openOffer(candidate: HotWindowCandidate) {
            offerCandidate.value = candidate
            layer.value = 'offer'
        }
        // «Предложить» у другого кандидата делает его текущим: время и
        // «Что предлагаем» в модалке считаются под него.
        function offerOther(candidate: HotWindowCandidate) {
            cursor.value = { ...cursor.value, [selectionKey.value]: candidates.value.indexOf(candidate) }
            startMinutes.value = null
            openOffer(candidate)
        }
        function openClient(candidate: HotWindowCandidate) {
            profileCandidate.value = candidate
            layer.value = 'client'
        }
        function onOfferSent({ candidate }: { candidate: HotWindowCandidate }) {
            const key = sentKey(candidate)
            if (!sent.value.includes(key)) sent.value = [...sent.value, key]
            layer.value = null
            showToast('Предложение отправлено')
        }

        function openServices(nextMode: 'settings' | 'filter') {
            servicesMode.value = nextMode
            layer.value = 'services'
        }
        function openFilters() {
            openServices('filter')
        }
        function openSettings() {
            layer.value = 'settings'
        }
        const allowedIds = computed(() => catalog.value.serviceList ?? defaultServiceIds(catalog.value.services))

        function setFilters(next: HotWindowsFilters) {
            filters.value = next
            saveFilters(projectId.value, next)
        }
        const isRefreshing = ref(false)
        async function refresh() {
            if (isRefreshing.value) return
            isRefreshing.value = true
            await loadAll(true, true)
            isRefreshing.value = false
            if (loadError.value) {
                showToast('Не удалось обновить подбор')
                return
            }
            const count = windows.value.length
            showToast(
                `Подбор обновлён · ${count} ${plural(count, 'окно', 'окна', 'окон')} на ${
                    day.value === 'today' ? 'сегодня' : 'завтра'
                }`
            )
        }

        function applyFilters(next: HotWindowsFilters) {
            setFilters(next)
            layer.value = null
            serviceId.value = null
            loadAll(true)
            showToast('Фильтр сохранён')
        }
        function resetFilters() {
            setFilters({ serviceIds: [], minPrice: 0 })
            serviceId.value = null
            loadAll(true)
            showToast('Фильтр сброшен')
        }
        function onServiceListSaved(next: HotWindowsSettings) {
            settings.value = next
            catalog.value = { ...catalog.value, serviceList: next.serviceList }
            // Фильтр живёт внутри набора: снятые из набора услуги из него уходят.
            const allowed = new Set(allowedIds.value)
            setFilters({ ...filters.value, serviceIds: filters.value.serviceIds.filter(id => allowed.has(id)) })
            layer.value = null
            serviceId.value = null
            loadAll(true)
            showToast('Набор услуг сохранён')
        }
        function onSettingsSaved(next: HotWindowsSettings) {
            settings.value = next
            layer.value = null
            loadAll(true)
            showToast('Настройки сохранены')
        }

        const toast = reactive({ visible: false, text: 'Готово' })
        let toastTimer: ReturnType<typeof setTimeout> | undefined
        function showToast(text: string) {
            toast.text = text
            toast.visible = true
            clearTimeout(toastTimer)
            toastTimer = setTimeout(() => (toast.visible = false), 3200)
        }

        function onKeydown(event: KeyboardEvent) {
            if (event.key !== 'Escape') return
            if (trialOpen.value) {
                trialOpen.value = false
                return
            }
            if (layer.value) layer.value = null
        }
        document.addEventListener('keydown', onKeydown)
        onBeforeUnmount(() => {
            document.removeEventListener('keydown', onKeydown)
            clearTimeout(toastTimer)
        })

        let liveStarted = false
        function startLive() {
            if (liveStarted) return
            liveStarted = true
            loadSettings()
            loadCatalog()
            loadCascades()
            loadAll()
        }

        watch(
            project,
            value => {
                if (!value?.id) return
                if (hasHotWindowsAccess(value)) {
                    mode.value = 'live'
                    startLive()
                } else {
                    mode.value = 'gate'
                    loadTrialStatus()
                }
            },
            { immediate: true }
        )

        const breadcrumbs = computed(() => [
            { text: project.value?.name ?? 'Проект', to: `/project/${projectId.value}` },
            { text: 'Горячие окна', to: '', disabled: true },
        ])
        const scheduleDate = computed(() => formatLongDate(currentDate.value))
        const updatedLabel = computed(() => formatUpdatedAt(fetchedAt.value[currentKey.value]))
        const filterCount = computed(() => activeFilterCount(filters.value))
        const filterSummaryText = computed(() =>
            filterSummary(filters.value, catalog.value.services, allowedIds.value)
        )
        const minGapLabel = computed(() => formatDuration(settings.value.minGapMinutes))
        const potentialLabel = computed(() =>
            formatMoney(windows.value.reduce((sum, item) => sum + (item.potentialRevenue || 0), 0))
        )
        const taskBadge = computed(() => {
            if (task.value === 'base') return 'Клиент без записи'
            const from = addDays(currentDate.value, 1)
            const sameMonth = from.slice(0, 7) === currentDate.value.slice(0, 7)
            return `Перенос: ${sameMonth ? Number(from.slice(8)) : formatShortDate(from)} → ${formatShortDate(
                currentDate.value
            )}`
        })
        const summarySlot = computed(() => {
            const item = currentWindow.value
            if (!item) return 'Нет свободного окна'
            return `${item.master} · ${item.time}–${item.timeTo} · ${formatDuration(item.durationMinutes)}`
        })
        const serviceCountLabel = computed(() => {
            const count = variants.value.length
            return count
                ? `${count} ${plural(count, 'подходящая услуга', 'подходящие услуги', 'подходящих услуг')}`
                : 'Подходящих услуг нет'
        })
        const candidateLimitLabel = computed(() => {
            const limit = settings.value.maxCandidatesPerWindow
            return `До ${limit} ${plural(limit, 'кандидата', 'кандидатов', 'кандидатов')} на услугу`
        })
        const emptyServices = computed(() => {
            if (currentData.value?.selection === 'empty') {
                return {
                    title: 'Выберите услуги для горячих окон',
                    text: 'Отметьте услуги, которые можно предложить отдельно.',
                    action: canEditSettings.value ? 'configure' : null,
                }
            }
            if (filterCount.value) {
                return {
                    title: 'Для этой задачи нет подходящих услуг',
                    text: 'Попробуйте уменьшить минимальную цену или изменить выбор услуг.',
                    action: 'reset',
                }
            }
            return {
                title: 'Для этой задачи нет подходящих услуг',
                text: 'Услуга должна помещаться в окно и иметь кандидатов.',
                action: canEditSettings.value && currentWindow.value ? 'configure' : null,
            }
        })
        const slotContext = computed(() =>
            currentVariant.value
                ? `${currentVariant.value.service.name} · ${candidatesLabel(candidates.value.length)}`
                : ''
        )
        const windowServicesLabel = (item: HotWindow) => {
            const count = item.variants.length
            return count
                ? `${count} ${plural(count, 'услуга', 'услуги', 'услуг')} с кандидатами`
                : 'Нет подходящих услуг'
        }
        const reason = (candidate: HotWindowCandidate) =>
            candidateReason(candidate, { start: start.value, referenceDate: currentDate.value })

        // Прямая проверка роли: checkRole шёл через таблицу permission с
        // redis-кэшем на 24ч, и кнопки появлялись только через сутки.
        const canEditSettings = computed(() => ['owner', 'admin'].includes(project.value?.myRole))

        return {
            projectId,
            mode,
            trialOpen,
            closeTrial,
            activatingTrial,
            activateTrial,
            showTrialButton,
            hotWindowsTrial,
            trialButtonText,
            buyModule,
            openFilters,
            openSettings,
            breadcrumbs,
            dayKeys: DAY_KEYS,
            dates,
            day,
            task,
            settings,
            catalog,
            catalogLoaded,
            filters,
            allowedIds,
            layer,
            servicesMode,
            offerCandidate,
            profileCandidate,
            currentData,
            currentDate,
            isCurrentLoading,
            isDayLoading,
            loadError,
            dayWindows,
            taskCount,
            windows,
            currentWindow,
            variants,
            visibleVariants,
            currentVariant,
            serviceSearch,
            candidates,
            best,
            others,
            length,
            bounds,
            start,
            toast,
            scheduleDate,
            updatedLabel,
            filterCount,
            filterSummaryText,
            minGapLabel,
            potentialLabel,
            taskBadge,
            summarySlot,
            serviceCountLabel,
            candidateLimitLabel,
            emptyServices,
            slotContext,
            canEditSettings,
            windowServicesLabel,
            reason,
            isSent,
            selectDayTask,
            selectWindow,
            selectService,
            changeStart,
            skipCandidate,
            openOffer,
            offerOther,
            openClient,
            onOfferSent,
            sendOptions,
            openServices,
            applyFilters,
            refresh,
            isRefreshing,
            resetFilters,
            onServiceListSaved,
            onSettingsSaved,
            showToast,
            candidatesLabel,
            visitsLabel,
            formatDuration,
            formatMoney,
            fromMinutes,
        }
    },
})
